import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import QuoteModal from '@/components/QuoteModal';
import LazyImage from '@/components/LazyImage';
import SEO from '@/components/SEO';
import { seoData } from '@/utils/seoData';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Wrench, Package } from 'lucide-react';

export const BrandsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedService, setSelectedService] = useState('');

  const handleBrandServiceClick = (brandName, serviceType) => {
    setSelectedBrand(brandName);
    setSelectedService(serviceType);
    setQuoteModalOpen(true);
  };

  const brands = [
    {
      name: 'Toyota',
      category: 'japanese',
      description: 'Complete parts and service for all Toyota models',
      popular: true,
      logo: '/images/brands/toyota.png',
    },
    {
      name: 'Honda',
      category: 'japanese',
      description: 'Genuine Honda parts and expert repairs',
      popular: true,
      logo: '/images/brands/honda.png',
    },
    {
      name: 'Suzuki',
      category: 'japanese',
      description: 'Authorized Suzuki parts distributor',
      popular: true,
      logo: '/images/brands/suzuki.png',
    },
    {
      name: 'Mazda',
      category: 'japanese',
      description: 'Mazda genuine parts supplier',
      popular: false,
      logo: '/images/brands/mazda.png',
    },
    {
      name: 'Nissan',
      category: 'japanese',
      description: 'Nissan parts and accessories',
      popular: false,
      logo: '/images/brands/nissan.png',
    },
    {
      name: 'Daihatsu',
      category: 'japanese',
      description: 'Premium auto parts designed for reliability and performance',
      popular: false,
      logo: '/images/brands/daihatsu.png',
    },
    {
      name: 'Mitsubishi',
      category: 'japanese',
      description: 'Efficient and durable parts for consistent reliability',
      popular: false,
      logo: '/images/brands/mitsubishi.png',
    },
    {
      name: 'Subaru',
      category: 'japanese',
      description: 'Specialized components for all-terrain vehicles',
      popular: false,
      logo: '/images/brands/subaru.png',
    },
    {
      name: 'Lexus',
      category: 'japanese',
      description: 'Reliable parts focused on comfort and luxury performance',
      popular: true,
      logo: '/images/brands/lexus.png',
    },
    {
      name: 'Isuzu',
      category: 'japanese',
      description: 'Heavy-duty parts for trucks and commercial vehicles',
      popular: false,
      logo: '/images/brands/isuzu.png',
    },
    {
      name: 'Hyundai',
      category: 'korean',
      description: 'Quality parts for all Hyundai vehicles',
      popular: true,
      logo: '/images/brands/hyundai.png',
    },
    {
      name: 'KIA',
      category: 'korean',
      description: 'KIA certified parts and service',
      popular: true,
      logo: '/images/brands/kia.png',
    },
    {
      name: 'FAW',
      category: 'chinese',
      description: 'FAW vehicle parts and maintenance',
      popular: false,
      logo: '/images/brands/faw.png',
    },
    {
      name: 'Changan',
      category: 'chinese',
      description: 'Changan authorized service center',
      popular: true,
      logo: '/images/brands/changan.png',
    },
    {
      name: 'DFSK',
      category: 'chinese',
      description: 'DFSK parts and repair services',
      popular: false,
      logo: '/images/brands/dfsk.png',
    },
    {
      name: 'MG',
      category: 'chinese',
      description: 'MG Motors parts supplier',
      popular: true,
      logo: '/images/brands/mg.png',
    },
    {
      name: 'Chery',
      category: 'chinese',
      description: 'Chery vehicles parts and repair',
      popular: false,
      logo: '/images/brands/chery.png',
    },
    {
      name: 'Prince',
      category: 'chinese',
      description: 'Prince Pearl parts available',
      popular: false,
      logo: '/images/brands/prince.png',
    },
    {
      name: 'Haval',
      category: 'chinese',
      description: 'Top-quality auto parts for reliable performance',
      popular: false,
      logo: '/images/brands/haval.png',
    },
    {
      name: 'Geely',
      category: 'chinese',
      description: 'Power and elegance with cutting-edge technology',
      popular: false,
      logo: '/images/brands/geely.png',
    },
    {
      name: 'BYD',
      category: 'chinese',
      description: 'Efficient EV and hybrid vehicle components',
      popular: true,
      logo: '/images/brands/byd.png',
    },
    {
      name: 'Proton',
      category: 'malaysian',
      description: 'Proton parts and service',
      popular: false,
      logo: '/images/brands/proton.png',
    },
    {
      name: 'United',
      category: 'local',
      description: 'United Motors service center',
      popular: true,
      logo: '/images/brands/united.png',
    },
    {
      name: 'Mercedes-Benz',
      category: 'german',
      description: 'High-quality parts for unmatched luxury and performance',
      popular: true,
      logo: '/images/brands/mercedes.png',
    },
    {
      name: 'BMW',
      category: 'german',
      description: 'Durable components designed for precision handling',
      popular: true,
      logo: '/images/brands/bmw.png',
    },
    {
      name: 'Audi',
      category: 'german',
      description: 'Advanced auto parts with cutting-edge technology',
      popular: true,
      logo: '/images/brands/audi.png',
    },
    {
      name: 'Volkswagen',
      category: 'german',
      description: 'German-engineered parts for long-lasting performance',
      popular: false,
      logo: '/images/brands/volkswagen.png',
    },
    {
      name: 'Chevrolet',
      category: 'american',
      description: 'Versatile components for various models',
      popular: false,
      logo: '/images/brands/chevrolet.png',
    },
    {
      name: 'Ford',
      category: 'american',
      description: 'Tough and dependable parts for all Ford vehicles',
      popular: true,
      logo: '/images/brands/ford.png',
    },
  ];

  const categories = [
    { id: 'all', label: 'All Brands' },
    { id: 'japanese', label: 'Japanese' },
    { id: 'korean', label: 'Korean' },
    { id: 'chinese', label: 'Chinese' },
    { id: 'german', label: 'German' },
    { id: 'american', label: 'American' },
    { id: 'malaysian', label: 'Malaysian' },
    { id: 'local', label: 'Local' },
  ];

  const filteredBrands =
    selectedCategory === 'all'
      ? brands
      : brands.filter((brand) => brand.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background noise-bg">
      <SEO 
        title={seoData.brands.title}
        description={seoData.brands.description}
        keywords={seoData.brands.keywords}
        ogImage={seoData.brands.ogImage}
        structuredData={seoData.brands.structuredData}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Supported <span className="gradient-text">Brands</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              We service and supply genuine parts for all major automotive brands available in Pakistan.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="pb-12">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                className={
                  selectedCategory === category.id
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                }
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Grid */}
      <section className="pb-20 lg:pb-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredBrands.map((brand, index) => (
              <Card
                key={index}
                className="card-3d bg-card border-border hover:border-primary/50 transition-all duration-300 relative"
              >
                {brand.popular && (
                  <div className="absolute top-3 right-3 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    Popular
                  </div>
                )}
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="w-full h-16 rounded-xl bg-white/90 flex items-center justify-center mb-4 p-2">
                    <LazyImage 
                      src={brand.logo} 
                      alt={`${brand.name} logo`} 
                      className="w-auto h-full object-contain brand-image"
                      wrapperClassName="w-full h-full flex items-center justify-center"
                      fadeInDuration={0.5}
                      spinnerLoader={true}
                    />
                  </div>
                  <h3 className="text-2xl font-semibold mb-2">{brand.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4 flex-grow">{brand.description}</p>
                  <div className="mb-4">
                    <span className="text-xs text-primary font-medium">
                      {categories.find((c) => c.id === brand.category)?.label}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto">
                    <Button
                      onClick={() => handleBrandServiceClick(brand.name, 'servicing')}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center gap-2"
                      size="sm"
                    >
                      <Wrench size={16} />
                      Servicing
                    </Button>
                    <Button
                      onClick={() => handleBrandServiceClick(brand.name, 'order-part')}
                      variant="outline"
                      className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground flex items-center justify-center gap-2"
                      size="sm"
                    >
                      <Package size={16} />
                      Order Part
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredBrands.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">No brands found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-12">Why Trust Us</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="text-4xl font-bold text-primary mb-2">29+</div>
                <div className="text-sm text-muted-foreground">Brands Supported</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">100%</div>
                <div className="text-sm text-muted-foreground">Genuine Parts</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">1 Year</div>
                <div className="text-sm text-muted-foreground">Warranty</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Can't Find Your Brand?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contact us directly. We work with a wide network of suppliers and can source parts for almost any vehicle.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quote Modal */}
      <QuoteModal 
        open={quoteModalOpen} 
        onOpenChange={setQuoteModalOpen}
        preSelectedBrand={selectedBrand}
        preSelectedService={selectedService}
      />

      <Footer />
    </div>
  );
};

export default BrandsPage;